import React, { HTMLAttributes, createContext, useContext } from 'react';

/**
 * The one card surface.
 *
 * Shape comes from `docs/design-system/elevated-polish-guide.md` > Card Style:
 * `rounded-2xl`, `shadow-sm` at rest / `shadow-md` on hover. They live here and
 * nowhere else — the rule is about WHERE the style lives, not about the values.
 *
 * TWO DELIBERATE DEPARTURES from the guide, both ruled 2026-09-18 (Dillan), both
 * in the direction of MORE contrast: the outline is `border-slate-300` rather
 * than the guide's `border-slate-200/80`, and `CardHeader` paints a `slate-200`
 * title band the guide does not describe at all. See each for why.
 */

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

const PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

/**
 * The card's padding step, published to `CardHeader`.
 *
 * `CardHeader` paints a header band that must run flush to the card's edges, so
 * it has to cancel exactly as much padding as the card applied — and only the
 * card knows which step is in force. Passing it through context keeps the two in
 * step automatically; a header that hard-coded `-mx-4` would misalign by 4px on
 * every `compact` card and by 8px on every `padding="lg"` one.
 */
const CardPaddingContext = createContext<CardPadding>('md');

/**
 * How `CardHeader` cancels the card's padding to bleed its band to the edges,
 * then re-applies its own, per step.
 *
 * `none` is the exception: that card applies no padding at all, so there is
 * nothing to cancel and nothing to gap afterwards — the band supplies its own
 * inset and sits directly on the content below, which is the full-bleed table
 * layout `noPadding` exists for.
 */
const HEADER_BLEED: Record<CardPadding, string> = {
  none: 'px-4 py-1.5 min-h-11',
  sm: '-mx-3 -mt-3 px-3 py-1.5 min-h-10 mb-3',
  md: '-mx-4 -mt-4 px-4 py-1.5 min-h-11 mb-4',
  lg: '-mx-6 -mt-6 px-6 py-2 min-h-12 mb-6',
};
// Height is set by `min-h-*`, NOT by the vertical padding (ruled 2026-09-23, Dillan).
// When padding set it, a band holding a `size="sm"` button (32px) came out ~14px
// taller than a title-only band (~18px of text), so two tiles side by side had
// visibly different header heights. The min-height is sized so a title-only band
// and a band with a small button are the SAME height; the padding is just enough
// to keep that button off the band's edges.

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Full-bleed content — a table, a scroll viewport, a divided list. */
  noPadding?: boolean;
  /** Tighten padding for dense list cards. Equivalent to padding="sm". */
  compact?: boolean;
  /**
   * Explicit padding step. Overrides `compact`; `noPadding` wins over both.
   * `padding="none"` means "this card supplies its own padding in className".
   * Padding never implies clipping — see `clip`.
   */
  padding?: CardPadding;
  /**
   * Clip children to the card's rounded box (`overflow-hidden`).
   *
   * OPT-IN, and deliberately so. Content that paints to the card's edge — a
   * table's header band, a divided list — squares off the radius unless the
   * card clips; that is what "the corners are cut off" looks like. But a clip
   * also eats any absolutely-positioned descendant that escapes the card's
   * box, and most dropdowns and popovers in this app are built that way rather
   * than through a portal.
   *
   * Defaulting this ON trades a visible cosmetic bug for an invisible
   * functional one: a popover that silently does not appear. So a card clips
   * only when a caller says it must, and `npm run audit:visual` flags a
   * full-bleed table in a card that forgot to.
   */
  clip?: boolean;
  /**
   * Card is a link or button target. Adds the guide's hover treatment —
   * `shadow-md`, a firmer border, and a 1px lift.
   */
  interactive?: boolean;
}

/**
 * forwardRef because cards get measured and scrolled to — a virtualiser sizing
 * a row, a page scrolling a section into view. Without it those call sites have
 * to hand-roll the class string, which is how the drift started.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { noPadding = false, compact = false, padding, clip, interactive = false, className = '', children, ...props },
  ref
) {
  const step: CardPadding = noPadding ? 'none' : padding ?? (compact ? 'sm' : 'md');

  // `border-slate-300`, not the guide's `border-slate-200/80`. Ruled 2026-09-18
  // (Dillan): at 80% opacity over a slate-50 page the outline was very nearly
  // invisible, so a column of tiles read as one undifferentiated sheet. A card's
  // edge has to be findable for the card to be a card.
  const classes = [
    'bg-white rounded-2xl border border-slate-300 shadow-sm',
    clip ? 'overflow-hidden' : '',
    // `slate-400` on hover, because the resting border moved to slate-300 —
    // the old `hover:border-slate-300` became a no-op against it and an
    // interactive card silently lost its "firmer edge" affordance.
    interactive
      ? 'hover:shadow-md hover:border-slate-400 hover:-translate-y-px transition-all duration-150'
      : '',
    PADDING_CLASSES[step],
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <CardPaddingContext.Provider value={step}>
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    </CardPaddingContext.Provider>
  );
});

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Opt out of the tinted band and render the header as a plain divider.
   *
   * For a card whose header is not a title bar — an inline control strip, a
   * header that already carries its own surface. Rare; the banded default is
   * what makes tiles read as one family.
   */
  plain?: boolean;
  /**
   * `office` paints the purple "office only" band for a tile only staff see on the FSR
   * review screen. It is purple, not amber, on purpose (Dillan, 2026-10-15): amber is the
   * report's Internal Reporting section (`ReportSection`'s `internalBadge`), and two
   * different things in one colour read as one. The body stays white: only the header
   * carries the colour. Ignored with `plain`, which has no band to colour.
   */
  tone?: 'default' | 'office';
}

/**
 * The card's title bar: a tinted band running flush to the card's edges, closed
 * by a divider a step darker than the card's own outline.
 *
 * Decided 2026-09-18 (Dillan). Before this, a `<Card>` + `<CardHeader>` tile had
 * no band at all while the hand-rolled panels beside it — `CollapsiblePanel` and
 * friends — painted a `bg-slate-50` one over a `rounded-xl` box with a
 * `border-slate-300` outline. Sitting side by side in a ticket's right rail, the
 * two read as two different components from two different apps: different corner
 * radius, different border weight, and only one with a header.
 *
 * The band is `slate-200`, arrived at by walking UP the scale twice: `slate-50`
 * (what the hand-rolled panels used) is invisible against a white card body, and
 * `slate-100` still read as "sort of" headed on a real page. `slate-200` is the
 * first step where the title bar is unambiguously a different surface from the
 * content under it, which is the whole point of banding it.
 *
 * It bleeds to the edge via negative margins sized from `CardPaddingContext`,
 * and rounds its own top corners to match the card's `rounded-2xl`. Rounding the
 * band itself — rather than making the card `clip` — is what keeps this change
 * from silently eating the popovers and dropdowns that escape a card's box, per
 * the `clip` note on `Card`.
 */
export function CardHeader({ className = '', plain = false, tone = 'default', children, ...props }: CardHeaderProps) {
  const step = useContext(CardPaddingContext);
  const band = tone === 'office' ? 'bg-purple-50 border-purple-200' : 'bg-slate-200 border-slate-300';

  const classes = plain
    ? `flex items-center justify-between pb-3 mb-3 border-b border-slate-200/80 ${className}`
    : `flex items-center justify-between ${band} border-b rounded-t-2xl ${HEADER_BLEED[step]} ${className}`;

  return (
    <div className={classes.replace(/\s+/g, ' ').trim()} {...props}>
      {children}
    </div>
  );
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

/**
 * Card titles match the Projects module: `text-sm font-semibold`.
 *
 * This read `text-[15px] font-bold` until 2026-09-18, on the strength of
 * `elevated-polish-guide.md` > Card Style ("card titles are font-bold (700), not
 * font-semibold"). Projects — the reference implementation this app converges on
 * — does not follow that line: its tile headings are `text-sm font-semibold
 * text-slate-900`, hand-rolled as `<h2>`/`<h3>` rather than through this
 * component. So a Field Service tile sat a pixel larger and a weight heavier
 * than the identical tile in Projects, which is what Dillan spotted.
 *
 * Ruled 2026-09-18 (Dillan): where the guide and Projects disagree, PROJECTS
 * WINS — it is the canon, the guide is the description. Changed here rather than
 * per-module so the two stop drifting apart again.
 */
export function CardTitle({ className = '', children, ...props }: CardTitleProps) {
  return (
    <h3 className={`text-sm font-semibold text-slate-900 leading-tight ${className}`.trim()} {...props}>
      {children}
    </h3>
  );
}
